import React, { useRef, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FiArrowLeft } from 'react-icons/fi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import hoastLogo from "../../../assets/images/HOAST-LOGO_2 1.png";
import EllipsisLoader from '../../../base_component/EllipsisLoader/EllipsisLoader';
import { verifyOTP, resendOTP, forgotPasswordOTP } from '../../../service/ApiServices';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from "uuid";

export default function Verification() {
    const navigate = useNavigate();
    const inputRefs = useRef([]);
    const { type } = useParams();
    const location = useLocation();
    const createAccountValues = location?.state?.createAccountValues;
    const forgotPasswordValues = location?.state?.email;
    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    let deviceId = localStorage.getItem("deviceIdHoast");

    if (!createAccountValues && !forgotPasswordValues) {
        navigate("/login");
    }

    useEffect(() => {
        if (!deviceId) {
            deviceId = uuidv4();
            localStorage.setItem("deviceIdHoast", deviceId);
        }
    }, [deviceId]);

    const getInitialTimerState = () => {
        const savedExpiryTime = localStorage.getItem('otpExpiryTime');
        if (savedExpiryTime) {
            const now = Date.now();
            const timeRemaining = Math.max(0, Math.ceil((parseInt(savedExpiryTime) - now) / 1000));
            if (timeRemaining > 0) {
                return { timeLeft: timeRemaining, isExpired: false };
            } else {
                return { timeLeft: 0, isExpired: true };
            }
        }
        return { timeLeft: 60, isExpired: false };
    };

    const [timeLeft, setTimeLeft] = useState(getInitialTimerState().timeLeft);
    const [isExpired, setIsExpired] = useState(getInitialTimerState().isExpired);

    useEffect(() => {
        if (timeLeft > 0 && !isExpired) {
            const timer = setTimeout(() => {
                setTimeLeft(prev => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (timeLeft === 0) {
            setIsExpired(true);
        }
    }, [timeLeft, isExpired]);

    useEffect(() => {
        if (timeLeft === 60 && !isExpired) {
            const expiryTime = Date.now() + (60 * 1000);
            localStorage.setItem('otpExpiryTime', expiryTime.toString());
        }
    }, []);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const formik = useFormik({
        initialValues: {
            otp: ['', '', '', ''],
        },
        validationSchema: Yup.object({
            otp: Yup.array()
                .of(Yup.string().matches(/^\d$/, 'Must be a digit').required('Required'))
                .length(4, 'Enter 4 digits')
                .test('all-filled', 'All 4 digits are required', (value) => value.every((v) => v !== '')),
        }),
        onSubmit: async (values) => {
            if (isExpired) {
                alert('OTP has expired. Please request a new one.');
                return;
            }

            setIsLoading(true);
            const code = values.otp.join('');

            if (type === "forgot") {
                const params = {
                    email: forgotPasswordValues || '',
                    otp: code
                };

                forgotPasswordOTP(params)
                    .then((res) => {
                        const message = res?.data?.message;
                        const status = res?.data?.status;

                        if (status === 1) {
                            setIsLoading(false);
                            toast.success(message);
                            navigate("/new_password", { state: { forgotPasswordValue: forgotPasswordValues } });
                        } else {
                            setIsLoading(false);
                            toast.error(message);
                        }
                    })
                    .catch((err) => {
                        setIsLoading(false);
                        const message = err?.response?.data?.message || 'OTP verification failed';
                        toast.error(message);
                    });
            } else {
                try {
                    const params = {
                        email: createAccountValues.email || '',
                        otp: code,
                        password: createAccountValues.password || '',
                        role: "artist",
                        first_name: createAccountValues.firstName || '',
                        last_name: createAccountValues.lastName || '',
                        accept_terms: true,
                        login_type: "Email",
                        device_type: "web",
                        device_token: "abcd1234",
                        device_id: deviceId
                    };

                    const response = await verifyOTP(params);
                    const message = response?.data?.message;
                    const status = response?.data?.status;

                    if (status === 1) {
                        setIsLoading(false);
                        toast.success(message);
                        localStorage.setItem("hoast_user_id", response?.data?.data?.id);
                        localStorage.setItem("is_free_plan_days", response?.data?.data?.free_tier_days);
                        navigate("/subscription", { state: { token: response?.data?.access_token } });
                    } else {
                        setIsLoading(false);
                        toast.error(message);
                    }
                } catch (error) {
                    setIsLoading(false);
                    const message = error?.response?.data?.message || 'OTP verification failed';
                    toast.error(message);
                }
            }
        },
    });

    const handleResendOTP = () => {
        setIsResending(true);

        const params = {
            email: type === "forgot" ? forgotPasswordValues : createAccountValues?.email,
        };

        resendOTP(params)
            .then((res) => {
                const message = res?.data?.message;
                const status = res?.data?.status;

                if (status === 1) {
                    toast.success(message);
                    setTimeLeft(60);
                    setIsExpired(false);
                    const expiryTime = Date.now() + (60 * 1000);
                    localStorage.setItem('otpExpiryTime', expiryTime.toString());
                    formik.resetForm();
                    inputRefs.current[0]?.focus();
                } else {
                    toast.error(message);
                }
            })
            .catch((err) => {
                const message = err?.response?.data?.message || 'Failed to resend OTP';
                toast.error(message);
            })
            .finally(() => {
                setIsResending(false);
            });
    };

    const handleChange = (index, value) => {
        if (!/^\d?$/.test(value)) return;

        const otp = [...formik.values.otp];
        otp[index] = value;
        formik.setFieldValue('otp', otp);

        if (value && index < 3) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && formik.values.otp[index] === '' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    return (
        <div className="h-screen flex flex-col items-center justify-center px-4">
            <div className="flex justify-center mb-4">
                <img src={hoastLogo} alt="Logo" className="md:h-36 h-24" />
            </div>

            <div className="bg-white md:p-8 p-5 rounded-xl shadow-md w-full max-w-xl">
                <h2 className="md:text-2xl text-xl font-semibold mb-1">Verification</h2>
                <p className="text-sm text-gray-500 mb-6">Enter the 4-digit code we sent to your email.</p>

                <div className="text-center mb-4">
                    {!isExpired ? (
                        ""
                    ) : (
                        <div className="text-sm text-red-500 font-medium">
                            OTP has expired
                        </div>
                    )}
                </div>

                <form onSubmit={formik.handleSubmit} className="space-y-6">
                    <div className="flex justify-center gap-4">
                        {formik.values.otp.map((digit, index) => {
                            const hasError = formik.touched.otp && !digit;
                            return (
                                <input
                                    key={index}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    ref={(el) => (inputRefs.current[index] = el)}
                                    onBlur={formik.handleBlur}
                                    disabled={isLoading || isExpired}
                                    className={`w-14 h-14 text-center border-2 rounded-xl text-xl focus:outline-none transition-colors
                                        ${hasError ? 'border-red-500' : 'border-gray-300'} 
                                        focus:border-[#24B565] disabled:opacity-50`}
                                />
                            );
                        })}
                    </div>

                    {formik.touched.otp && typeof formik.errors.otp === 'string' && (
                        <div className="text-red-500 text-xs text-center">{formik.errors.otp}</div>
                    )}

                    <div className="text-sm text-center text-[#667085] space-y-2">
                        <div>
                            Didn't receive the code?{' '}
                            <button
                                type="button"
                                onClick={handleResendOTP}
                                disabled={!isExpired || isResending}
                                className={`font-medium ${isExpired && !isResending
                                    ? 'text-[#F48B94] cursor-pointer'
                                    : 'text-gray-400 cursor-not-allowed'
                                    }`}
                            >
                                {isResending ? (
                                    <div className="flex items-center gap-2">
                                        Resending...
                                        <div className="w-4 h-4 border-2 border-[#F48B94] border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                ) : isExpired ? (
                                    'Resend'
                                ) : (
                                    <> Resend in <span className="text-[#F48B94]">{formatTime(timeLeft)}</span></>
                                )}
                            </button>
                        </div>
                    </div>

                    <p className="text-sm text-start text-[#667085]">
                        <span className="text-[#F48B94]">NOTE: </span>
                        If you haven't received the code, please check your spam folder.
                    </p>

                    <button
                        type="submit"
                        disabled={formik.isSubmitting || isLoading || !formik.values.otp.every(digit => digit !== '') || isExpired}
                        className={`w-full h-[52px] rounded-xl cursor-pointer transition duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed ${formik.values.otp.every(digit => digit !== '') && !isExpired
                            ? 'bg-black text-white'
                            : 'bg-[#F0F1F3] text-gray-500'
                            }`}
                    >
                        {isLoading ? (
                            <EllipsisLoader color={formik.values.otp.every(digit => digit !== '') && !isExpired ? "white" : "red"} />
                        ) : isExpired ? (
                            'OTP Expired'
                        ) : (
                            'Continue'
                        )}
                    </button>

                    <div
                        onClick={() => navigate(type == "forgot" ? "/login" : "/create_account")}
                        className="flex items-center justify-center mt-6 text-sm text-gray-700 cursor-pointer"
                    >
                        <FiArrowLeft className="mr-2" />
                        Go back
                    </div>
                </form>
            </div>
        </div>
    );
}
